import _ from 'underscore';
import Promise from 'bluebird';
import { useRef, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Scopes } from '@bingads-webui-campaign-react/goal-selector';
import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { mergeData } from '@bingads-webui-campaign-react/product-services';
import { getFeedData } from '@bingads-webui-campaign/audience-campaign-creation-wizard';
import { withDFMCache } from '@bingads-webui/dfm-cache';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { getErrorsPlaceholders, DeviceKeys } from '@bingads-webui-campaign-react/xandr-campaign';
import { getBudgetRange } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';
import biddingStrategyTypes from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';

import { isSearchShoppingCampaign, getCampaignType, allSettledPromiseWrapper, getNoValidStore } from '../step-utils';
import { saveCampaign, loadCampaign, modelToViewModelForCampaign } from '../../model';
import { CREATE_SCENARIO } from '../../constants';
import { useWizardLog, LOG_STATUS, getProductData } from '../../shared';

const getDynamicFeedData = withDFMCache(getFeedData, 'getFeedScopeDFM', {});
const AudienceCampaignNamePrefix = _TL_('Shopping_Audience');
const SearchCampaignNamePrefix = _TL_('Shopping_Search');

export const getDefaultCampaign = ({
  isSearch,
  currencyCode,
  i18n,
}) => ({
  Name: generateNameSuggestionSuffix(i18n.getString(isSearch ? SearchCampaignNamePrefix : AudienceCampaignNamePrefix)),
  Status: 'Paused',
  Budget: {
    Amount: getBudgetRange(currencyCode).Daily.Default,
    Type: 'DailyStandard',
  },
  BiddingScheme: {
    Type: 'EnhancedCpc',
  },
  Languages: ['All'],
  conversionGoals: {
    originalConversionGoals: [],
    goalSelection: [],
    goalScopeSelection: Scopes.ACCOUNT,
  },
  BudgetId: null,
  ProviderId: undefined,
  SalesCountry: null,
  FeedLabel: undefined,
  LocalInventoryAdsEnabled: false,
  finalUrlExpansionOptOut: false,
  ShoppableAdsEnabled: false,
  Priority: 'Low',
  ...isSearch ? {
    devices: [{
      device: DeviceKeys.Computers,
      bidAdjustment: 0,
    }, {
      device: DeviceKeys.Tablets,
      bidAdjustment: 0,
    }, {
      device: DeviceKeys.Smartphones,
      bidAdjustment: 0,
    }],
  } : {},
  ...getCampaignType(isSearch),
});

export const useCampaignRepository = () => {
  const productFilterRef = useRef();
  const storeSettingRef = useRef();
  const i18n = useContext(I18nContext);
  const [siteExclusionView, setSiteExclusionView] = useState(null);
  const [ipExclusionView, setIPExclusionView] = useState(null);
  const { state, send } = useWizardService();
  const { flow, createScenario } = state.context.wizardData;
  const isCampaignCreate = createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN;
  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);
  const isShoppableAdsEnabled = _.get(serverContext, ['DynamicPermissions', 'ShoppableAds'], false);
  const isOdataGetProductScopeEnabled = _.get(serverContext, ['DynamicPermissions', 'OdataGetProductScope'], false);
  const currencyCode = _.get(serverContext, ['CurrentAccount', 'CurrencyCode'], 'USD');
  const isSearch = isSearchShoppingCampaign(flow);
  const { scope } = useContext(ScopeContext) || {};
  const { onStepLoadedLog, onStepSavedLog } = useWizardLog({ activity, wizardState: state, isSearch });
  const { cid, aid } = scope.values;
  const [savedCampaignId, setSavedCampaignId] = useState(null);
  const AlertCenter = useMemo(() => new AlertHelper({ i18n }), [i18n]);
  const logProductApi = (spendTime, scenario) => activity.signal(`productApiSpend-${isCampaignCreate ? 'create' : 'setting'}-${isSearch ? 'search' : 'audience'}-${scenario}: ${spendTime}`);

  const ipExclusionViewRef = useRef();
  const siteExclusionViewRef = useRef();
  const productConditionsRef = useRef();

  const internalGetProductData = useCallback(params => getProductData(params, serverContext), [serverContext]);
  const getAudienceProductDataWithCache = withDFMCache(internalGetProductData, 'getAudienceProductScopeDFM', {});
  const getProductDataWithCache = withDFMCache(internalGetProductData, 'getProductScopeDFM', {});

  const getCampaignFromViewModel = useCallback(({ campaignModel, productData, feedData }) => modelToViewModelForCampaign({
    campaignModel,
    locationCriterionsModel: [],
    dayTimeCriterionsModel: [],
    targetCriterionsModel: [],
    bundleCountries: [],
    productData,
    feedData,
    permissions: serverContext.Permissions,
  }).data, [serverContext.Permissions]);

  useEffect(() => {
    if (ipExclusionView) {
      ipExclusionViewRef.current = ipExclusionView;
    }
    if (siteExclusionView) {
      siteExclusionViewRef.current = siteExclusionView;
    }
  }, [siteExclusionView, ipExclusionView]);

  const load = () => {
    onStepLoadedLog({ status: LOG_STATUS.LOAD_START });
    const {
      campaign: savedCampaign,
      productData: savedProductData,
      feedData: savedFeedData,
    } = state.context.wizardData;

    const campaignId = savedCampaign ? savedCampaign.Id : undefined;

    const productParams = campaignId ? { campaignId } : {};
    // in setting page.
    if (!isCampaignCreate) {
      const savedSalesCountry = _.get(savedCampaign, ['CampaignSettings', 0, 'SalesCountry']);
      const savedFeedLabel = _.get(savedCampaign, ['CampaignSettings', 0, 'FeedLabel']);

      if (savedSalesCountry) {
        productParams.salesCountry = savedSalesCountry;
      }
      if (savedFeedLabel) {
        productParams.feedLabel = savedFeedLabel;
      }
      if (!isSearch) {
        productParams.getCategoryFullData = true;
        productParams.getProductTypeFullData = true;
        productParams.salesCountry = 'US'; // For audience shopping. We only support US for now.
      }
    } else {
      productParams.getProductConditionOptions = true;
      productParams.getSalesCountryCollection = true;
      productParams.getDynamicDataFeedOptions = true;

      if (!isSearch) {
        productParams.getCategoryFullData = true;
        productParams.getProductTypeFullData = true;
        productParams.salesCountry = 'US'; // For audience shopping. We only support US for now.
      }
    }

    const loadPromises = {};
    if (campaignId) {
      if (isCampaignCreate) {
        loadPromises.campaign = Promise.resolve(savedCampaign);
      } else {
        loadPromises.campaign = loadCampaign(cid, aid, campaignId);
      }
    } else {
      loadPromises.campaign = getDefaultCampaign({ isSearch, currencyCode, i18n });
    }
    if (!isSearch) {
      loadPromises.feedData = savedFeedData ? Promise.resolve(savedFeedData) : getDynamicFeedData();
    }
    let productCall = null;
    if (!savedProductData) {
      const req = isSearch ? getProductDataWithCache(productParams) : getAudienceProductDataWithCache(productParams);
      productCall = () => {
        const nTime = Date.now();
        return req.then((pData) => {
          logProductApi(Date.now() - nTime, 'load');

          if (isOdataGetProductScopeEnabled) {
            const mergedData = mergeData(pData);

            return mergedData;
          }

          return pData;
        });
      };
    } else {
      productConditionsRef.current = savedProductData.ProductConditionOptions;
      productCall = () => Promise.resolve(savedProductData);
    }

    return Promise.props({
      ...loadPromises,
      productData: productCall(),
    }).then(({
      campaign,
      productData,
      feedData,
    }) => {
      onStepLoadedLog({ status: LOG_STATUS.LOAD_SUCCESS, msg: getNoValidStore(productData, feedData) ? 'NoStore' : 'HasStore' });
      const productDataSource = productData;
      productDataSource.ProductCollectionAccounts = _.filter(productData.ProductCollectionAccounts, store => !store.IsCoOpStore);
      productConditionsRef.current = productData.ProductConditionOptions;
      return {
        campaign: isCampaignCreate ? campaign : getCampaignFromViewModel({
          campaignModel: campaign,
          productData,
          feedData,
        }),
        productData: productDataSource,
        productConditionsRef, // when salesCountry change, we should save the productConditions and update the productData
        feedData: feedData || {},
        productFilterRef,
        storeSettingRef,
        ipExclusionViewRef,
        siteExclusionViewRef,
        setSiteExclusionView,
        setIPExclusionView,
      };
    });
  };

  const save = ({ data }) => {
    const {
      campaign,
      productData,
      productConditionsRef: savedProductConditions,
    } = data;
    onStepSavedLog({ status: LOG_STATUS.SAVE_START });
    let model = _.extend({}, campaign);
    const isEditMode = campaign.Id || savedCampaignId;

    if (savedCampaignId) {
      model.Id = savedCampaignId;
    }

    // In campaign settings page, support swtich between CPS and Non-CPS.
    if (isEditMode) {
      const isCostPerSaleEnabled = _.get(serverContext, ['Permissions', 'IsCostPerSaleEnabled'], false);

      if (campaign.BiddingScheme && campaign.BiddingScheme.Type === biddingStrategyTypes.costPerSale) {
        model.PersonalizedOffersEnabled = true;
        model.CashbackPercent = 10;
        model.CashbackScope = 'Product';
        model.Priority = 'High';
      } else if (isCostPerSaleEnabled && model.CampaignSubType !== CampaignSubTypes.AudienceDynamicFeedBased) {
        // PersonalizedOffersEnabled should be set to true/false only when CPS pilot is enabled, otherwise it will cause PersonalizedOffersAccountNotInPilot error.
        model.PersonalizedOffersEnabled = false;
        model.CashbackPercent = 'delete_value';
        model.CashbackScope = 'delete_value';
      } else {
        // This is for AudienceDynamicFeedBased campaign or when CPS pilot is not enabled.
        delete model.PersonalizedOffersEnabled;
        delete model.CashbackPercent;
        delete model.CashbackScope;
      }
    }

    const storeSettingView = storeSettingRef.current;
    const productFilterView = productFilterRef.current;

    const isStoreValid = storeSettingView.validate();
    const isProductFilterValid = productFilterView.validate();

    if (!isStoreValid || !isProductFilterValid) {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaignStepIsValid: false });
      return Promise.reject();
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaignStepIsValid: true });

    if (!isSearch && !campaign.Id) {
      if (storeSettingView.isSelectedStore() || storeSettingView.selectedCategory === null) {
        model.CampaignSubType = CampaignSubTypes.AudienceFeedBased;
      } else {
        model.CampaignSubType = CampaignSubTypes.AudienceDynamicFeedBased;
        model.FeedId = storeSettingView.viewModel.FeedId();
      }
    }

    const storeSettingModel = storeSettingView.viewModel.toObject();
    // we don't need get Priority from storeSettingModel, since we separate it as an independent field.
    _.extend(model, _.omit(storeSettingModel, 'Priority'));

    const productScope = productFilterView.getProductScope();
    const hasFilteredProducts = productFilterView.hasFilteredProducts();

    let SalesCountryValue = model.SalesCountry;
    if (storeSettingView.isFeedProductFilterEnabled) {
      if (!storeSettingView.viewModel.isFeedFilterChecked()) {
        // choose neither feed label nor sales country
        model = _.omit(model, 'SalesCountry');
        model = _.omit(model, 'FeedLabel');
        SalesCountryValue = null;
      } else if (model.FeedLabel && model.FeedLabel.length > 0) {
        model = _.omit(model, 'SalesCountry');
        SalesCountryValue = null;
      } else {
        model = _.omit(model, 'FeedLabel');
      }
      if (isEditMode && !model.SalesCountry) {
        model.SalesCountry = 'delete_value';
      }
      if (isEditMode && !model.FeedLabel) {
        model.FeedLabel = 'delete_value';
      }
    }
    if (!isShoppableAdsEnabled) {
      model = _.omit(model, 'ShoppableAdsEnabled');
    }
    if (!isSearch) {
      model = _.omit(model, 'Priority');
    }
    if (hasFilteredProducts) {
      model.CampaignProductScope = _.defaults(productScope, {
        CampaignName: campaign.Name,
      });

      if (productData.CampaignCriterionsData) {
        model.CampaignProductScope.Id = productData.CampaignCriterionsData.Id;
      }
    } else {
      model = _.omit(model, 'CampaignProductScope');
    }
    const destroyProductScopeIfNeed = () => {
      if (productData.CampaignCriterionsData && !hasFilteredProducts) {
        return jsdata.CampaignProductScope.destroy(productData.CampaignCriterionsData.Id, { adapter: 'bulk', parentId: campaign.Id });
      }
      return Promise.resolve();
    };

    if (isEditMode) {
      model = _.omit(model, 'devices');
    }

    const saveCampaignPromise = saveCampaign({
      campaign: { data: model },
      updateCampaignStatus: isCampaignCreate,
      // adGroup: isEditMode ? null : { data: getAdGroup(campaign) }, // will create ad group only for first time
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    });
    const saveExtensionsInSettingFlow = () => {
      if (isCampaignCreate) {
        return Promise.resolve();
      }
      const extensionData = {
        campaignId: campaign.Id,
        AdvanceSettings: {
          Exclusions: {
            ExcludedSites: siteExclusionView.getValues(),
            ExcludedIPs: ipExclusionView.getValues(),
          },
        },
      };
      return exclusionRepository.saveExtensions(extensionData, 'campaign')
        .then(() => {
          siteExclusionView.clearApiErrors();
        })
        .catch((response) => {
          if (response.NegativeSitesResult || response.NegativeIPsResult) {
            const { NegativeSitesResult: siteErrors, NegativeIPsResult: ipErrors } = response;
            if (siteExclusionView && siteErrors) {
              siteExclusionView.showErrors(siteErrors);
            } else {
              siteExclusionView.clearApiErrors();
            }
            if (ipExclusionView && ipErrors) {
              ipExclusionView.showErrors(ipErrors);
            } else {
              ipExclusionView.clearApiErrors();
            }
            // eslint-disable-next-line no-throw-literal
            throw 'exclusion error';
          }
        });
    };
    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper([saveCampaignPromise, destroyProductScopeIfNeed(), saveExtensionsInSettingFlow()]).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');
        if (allSuccess) {
          if (isEditMode) {
            const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
              scenarioName: 'BudgetBidCampaignImportNotification',
              activityName: 'ShoppingCampaignSettingPage',
            });

            const { campaign: initialCampaign } = state.context.stepContexts.CAMPAIGN_SETTINGS.initialData;

            showBudgetBidCampaignNotifications({
              campaignItemList: [initialCampaign],
              oldBudget: initialCampaign.Budget,
              newBudget: campaign.Budget,
              oldBidStrategy: initialCampaign.BiddingScheme,
              newBidStrategy: campaign.BiddingScheme,
              activity: BudgetBidCampaignImportNotificationActivity,
            });
          }
          if (!isCampaignCreate) {
            send(WIZARD_EVENTS.EXIT);
            return resolve();
          }
          const campaignData = {
            ...results[0].value.campaign.data,
            SalesCountry: SalesCountryValue,
            FeedLabel: model.FeedLabel === 'delete_value' ? '' : model.FeedLabel,
          };

          const wizardUpdateData = {
            campaign: campaignData,
            feedData: data.feedData,
            productData,
          };
          const stepProps = {
            ...data,
            campaign: campaignData,
            productData,
            ...getErrorsPlaceholders(),
          };
          // if no product filtered, we don't need to get CampaignCriterions data.
          if (!hasFilteredProducts) {
            onStepSavedLog({ status: LOG_STATUS.SAVE_SUCCESS, msg: `Status: ${campaignData.Status}, Id: ${campaignData.Id}` });
            wizardUpdateData.productData = { ...productData, CampaignCriterionsData: null };
            stepProps.productData = { ...productData, CampaignCriterionsData: null };
            if (model.FeedId) {
              return loadCampaign(cid, aid, campaignData.Id).then((fullCampaign) => {
                const fullCampaignData = getCampaignFromViewModel({
                  campaignModel: fullCampaign,
                  productData,
                  feedData: data.feedData,
                });
                send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { ...wizardUpdateData, campaign: fullCampaignData });
                resolve({ ...stepProps, campaign: fullCampaignData });
              });
            }
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, wizardUpdateData);
            return resolve(stepProps);
          }

          const nTime = Date.now();
          return internalGetProductData({ campaignId: campaignData.Id, getCampaignCriterionsOnly: true }).then((pData) => {
            logProductApi(Date.now() - nTime, 'save');

            const newProductData = _.isArray(pData) ? mergeData(pData) : pData;

            onStepSavedLog({ status: LOG_STATUS.SAVE_SUCCESS, msg: `Status: ${campaignData.Status}, Id: ${campaignData.Id}` });
            if (campaignData.CampaignProductScope && newProductData.CampaignCriterionsData) {
              campaignData.CampaignProductScope.Id = newProductData.CampaignCriterionsData.Id;
            }
            const updatedProductData = {
              ...productData,
              CampaignCriterionsData: newProductData.CampaignCriterionsData,
              ProductConditionOptions: savedProductConditions.current || productData.ProductConditionOptions,
            };

            wizardUpdateData.productData = updatedProductData;
            wizardUpdateData.campaign = campaignData;

            stepProps.productData = updatedProductData;
            stepProps.campaign = campaignData;

            // if select dynamic feed and back to edit. We need to get the ProviderId from campaign setting. which not return from bulk upsert.
            if (model.FeedId) {
              return loadCampaign(cid, aid, campaignData.Id).then((fullCampaign) => {
                const fullCampaignData = getCampaignFromViewModel({
                  campaignModel: fullCampaign,
                  productData: updatedProductData,
                  feedData: data.feedData,
                });
                send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { ...wizardUpdateData, campaign: fullCampaignData });
                resolve({ ...stepProps, campaign: fullCampaignData });
              });
            }
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, wizardUpdateData);
            return resolve(stepProps);
          }).catch((error) => {
            onStepSavedLog({ status: LOG_STATUS.SAVE_FAIL, msg: error });
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, wizardUpdateData);
            AlertCenter.showAlert(_.get(error, ['Errors', 0, 'Message']));
            return reject();
          });
        }

        // bulkupsert might be failed in some column. if campaign column is success. we should save campaignId to avoid duplicate campaign creation error.
        const campaignId = _.get(results[0], ['reason', 'campaign', 'data', 'Id']);
        if (campaignId > 0) {
          setSavedCampaignId(campaignId);
        }
        // const adGroupId = _.get(results[0], ['reason', 'adGroup', 'data', 'Id']);
        // if (adGroupId > 0) {
        //   send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        //     adGroup: results[0].reason.adGroup.data,
        //   });
        // }

        const errors = _.map(results, (result) => {
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          return serverErrors;
        });
        onStepSavedLog({ status: LOG_STATUS.SAVE_FAIL, msg: results });
        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};
